const {
  FACEBOOK,
  GDN,
  GTM,
  KNOWLEDGE_CAMPUS,
  PEARCH_ADSENSE,
  OUTBRAIN,
  TABOOLA,
  WBT,
} = require('../utils/constants.js');
const { hasKeys, hasAnyKey } = require('../utils/sharedFunctions.js');

class ClientPixelHandler {
  constructor(window, document, adType, ocodePayload, adContext) {
    this.win = window;
    this.doc = document;
    this.adType = adType;
    this.adContext = adContext;
    this.ocodePayload = ocodePayload;
    this.pageParams = this.getParamsFromPage();
  }

  firePageViewPixel() {
    if (this.shouldFirePageView(TABOOLA)) {
      this.fireTaboolaPageView();
    }

    if (this.shouldFirePageView(FACEBOOK)) {
      this.fireFacebookPageView();
    }

    if (this.shouldFirePageView(OUTBRAIN)) {
      this.fireOutbrainPageView();
    }

    if (this.shouldFirePageView(PEARCH_ADSENSE)) {
      this.firePearchAdsensePageView();
    }

    if (this.shouldFirePageView(GDN)) {
      this.fireGDNPageView();
    }

    if (this.shouldFirePageView(GTM)) {
      this.fireGTMPageView();
    }

    if (this.shouldFirePageView(WBT)) {
      this.fireWBTPageView();
    }
  }

  fireConversionPixel(linkIdx) {
    if (this.shouldFireConversion(TABOOLA)) {
      this.fireTaboolaConversion();
    }

    if (this.shouldFireConversion(FACEBOOK)) {
      this.fireFacebookConversion();
    }

    if (this.shouldFireConversion(OUTBRAIN)) {
      this.fireOutbrainConversion();
    }

    if (this.shouldFireConversion(PEARCH_ADSENSE)) {
      this.firePearchAdsenseConversion();
    }

    if (this.shouldFireConversion(GDN)) {
      this.fireGDNConversion();
    }
    if (this.shouldFireConversion(GTM)) {
      this.fireGTMConversion();
    }

    if (this.shouldFireConversion(WBT)) {
      this.fireWBTConversion(linkIdx);
    }

    if (this.shouldFireConversion(KNOWLEDGE_CAMPUS)) {
      this.fireKnowledgeCampusConversion();
    }
  }

  shouldFirePageView(pixelType) {
    if (!this.ocodePayload) {
      throw new Error('Must provide ocode to fire pixel');
    }
    const overrides = this.ocodePayload.config.default;
    switch (pixelType) {
      case TABOOLA:
        return hasKeys(overrides, [
          'component.PartialPageViewPixel.taboolaPixel',
          'component.PartialPageViewPixel.taboolaPixelId',
        ]);
      case FACEBOOK:
        return hasKeys(overrides, ['component.PartialPageViewPixel.facebookTrackingId']);
      case OUTBRAIN:
        return hasKeys(overrides, [
          'component.PartialPageViewPixel.outbrainPixel',
          'component.PartialPageViewPixel.outbrainPixelId',
        ]);
      case PEARCH_ADSENSE:
        return hasKeys(overrides, [
          'component.PartialPageViewPixel.pearchAdsensePixel',
          'gslClient',
          'component.CSATextAdConfigurationPartial.desktopStyleId',
        ]);
      case GDN:
        return hasKeys(overrides, ['component.PartialPageViewPixel.gdnRemarketingTrackingId']);
      case GTM:
        return hasAnyKey(overrides, [
          'component.AgencyGtm.agencyGtmId',
          'component.PartialPageViewPixel.agencyGtmId',
        ]);
      case WBT:
        return hasKeys(overrides, ['component.PartialPageViewPixel.wbtPixel']);
      default:
        return false;
    }
  }

  shouldFireConversion(pixelType) {
    if (!this.ocodePayload) {
      throw new Error('Must provide ocode to fire pixel');
    }

    const overridePrefix = this.getOverridePrefix();
    if (!overridePrefix) throw new Error(`Ad type "${this.adType}" not supported by pixel handler.`);
    const overrides = this.ocodePayload.config.default;
    switch (pixelType) {
      case TABOOLA:
        return hasKeys(overrides, [
          `${overridePrefix}.taboolaPixel`,
          `${overridePrefix}.taboolaPixelId`,
        ]);
      case FACEBOOK:
        return hasKeys(overrides, [`${overridePrefix}.facebookTrackingId`]);
      case OUTBRAIN:
        return hasKeys(overrides, [
          `${overridePrefix}.outbrainPixel`,
          `${overridePrefix}.outbrainPixelId`,
        ]);
      case PEARCH_ADSENSE:
        return hasKeys(overrides, [`${overridePrefix}.pearchAfsFbEvents`]);
      case GDN:
        return hasKeys(overrides, [
          `${overridePrefix}.gdnConversionId`,
          `${overridePrefix}.gdnConversionGtagId`,
        ]);
      case GTM:
        return hasAnyKey(overrides, [
          'component.AgencyGtm.agencyGtmId',
          'component.PartialPageViewPixel.agencyGtmId',
        ]);
      case WBT:
        return hasKeys(overrides, [`${overridePrefix}.wbtPixel`]);
      case KNOWLEDGE_CAMPUS:
        return hasKeys(overrides, [`${overridePrefix}.knowledgeCampusPixel`]);
      default:
        return false;
    }
  }

  getOverridePrefix() {
    const prefixByAdType = {
      google: 'component.PartialConversionPixel',
      bing: 'component.PartialBingAds',
      yahoo: 'component.PartialYahooAds',
    };

    return prefixByAdType[this.adType];
  }

  getOcodeValue(key) {
    return this.ocodePayload.config.default[key];
  }

  getParamsFromPage() {
    const sourceUrl = this.win.location.toString();

    const { searchParams } = new URL(sourceUrl);
    const utmContent = searchParams.get('utm_content');

    const cookiesObj = this.getDocumentCookies();
    let monetizeParamObj = {};

    if (utmContent && utmContent.includes('params:')) {
      // 7 because of the length of string 'params:'
      const utmParams = utmContent.substring(7);
      monetizeParamObj = Object.fromEntries(new URLSearchParams(utmParams));
    } else if (cookiesObj.utm_content && cookiesObj.utm_content.includes('params:')) {
      const utmCookieParams = cookiesObj.utm_content.substring(7);
      monetizeParamObj = Object.fromEntries(new URLSearchParams(utmCookieParams));
    } else if (cookiesObj.iac_an) {
      monetizeParamObj.ad = cookiesObj.iac_ad;
      monetizeParamObj.ag = cookiesObj.iac_ag;
      monetizeParamObj.an = cookiesObj.iac_an;
      monetizeParamObj.rch = cookiesObj.iac_rch;
      monetizeParamObj.crch = cookiesObj.iac_crch;
      monetizeParamObj.o = cookiesObj.iac_o;
    }

    // direct params have the highest priority,
    // we will overwrite any previously set monetizeParamObj here
    const urlParamsObj = Object.fromEntries(searchParams);
    Object.assign(monetizeParamObj, urlParamsObj);

    return monetizeParamObj;
  }

  getDocumentCookies() {
    const { cookie } = this.doc;
    return cookie.split('; ').reduce((accum, current) => {
      const [name, ...value] = current.split('=');
      /* eslint-disable-next-line */
      accum[name] = value.join('=');
      return accum;
    }, {});
  }
}

ClientPixelHandler.prototype.fireTaboolaPageView = require('./taboola.js').fireTaboolaPageView;
ClientPixelHandler.prototype.fireTaboolaConversion = require('./taboola.js').fireTaboolaConversion;

ClientPixelHandler.prototype.fireOutbrainPageView = require('./outbrain.js').fireOutbrainPageView;
ClientPixelHandler.prototype.fireOutbrainConversion = require('./outbrain.js').fireOutbrainConversion;

ClientPixelHandler.prototype.fireFacebookPageView = require('./facebook.js').fireFacebookPageView;
ClientPixelHandler.prototype.fireFacebookConversion = require('./facebook.js').fireFacebookConversion;

ClientPixelHandler.prototype.getPearchPayload = require('./pearchAdsense.js').getPearchPayload;
ClientPixelHandler.prototype.firePearchAdsensePageView = require('./pearchAdsense.js').firePearchAdsensePageView;
ClientPixelHandler.prototype.firePearchAdsenseConversion = require('./pearchAdsense.js').firePearchAdsenseConversion;

ClientPixelHandler.prototype.mountGTMContainer = require('./gdn.js').mountGTMContainer;
ClientPixelHandler.prototype.fireGDNPageView = require('./gdn.js').fireGDNPageView;
ClientPixelHandler.prototype.fireGDNConversion = require('./gdn.js').fireGDNConversion;

ClientPixelHandler.prototype.fireWBTPageView = require('./wbt.js').fireWBTPageview;
ClientPixelHandler.prototype.fireWBTConversion = require('./wbt.js').fireWBTConversion;

ClientPixelHandler.prototype.fireKnowledgeCampusConversion = require('./knowledgeCampus.js').fireKnowledgeCampusConversion;

ClientPixelHandler.prototype.mountAgencyGTMContainer = require('./agencyGtm.js').mountAgencyGTMContainer;
ClientPixelHandler.prototype.fireGTMPageView = require('./agencyGtm.js').fireGTMPageView;
ClientPixelHandler.prototype.fireGTMConversion = require('./agencyGtm.js').fireGTMConversion;
ClientPixelHandler.prototype.getAgencyGTMId = require('./agencyGtm.js').getAgencyGTMId;

module.exports = ClientPixelHandler;
